/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import mbpLogger from '@module-logger/mbp';
import {
    array, bool, func, object, string,
} from 'prop-types';

import { useSelector } from 'react-redux';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import noop from '../../../../../helpers/noop';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import PLACollectionRecsContainer from './PLACollectionRecsContainer';
import PLAPDPIconQuery from './PLAPDPIconQuery';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';

export default function PLACollectionRecsABWrapper({
    plaRedesignDesktop,
    brand,
    partNumber,
    productName,
    skuData,
}) {
    const isPlaPdpIconEnabled = useSelector(getFeatureFlag('is-pla-pdp-icons-enabled'));
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaEnabled = useIsPlaPage() || isDisplayChannel;
    const {
        loading, error, data: wrapperData, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'PLACollectionRecsABWrapper',
        interactionName: '1800flowers - Get Campaign - PLA Collection Recs Wrapper',
        targeting: {
            pageType: 'product',
        },
        optInHoldoutParticipation: true,
        resolveIdentity: true,
        featureFlag: isPlaEnabled && isPlaPdpIconEnabled,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: wrapperData?.contentSF,
        isFireImpression: true,
        page: { type: 'product' },
    });

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: wrapperData?.contentSF,
        isFireImpression: false,
        page: { type: 'product' },
    });

    if (loading || variables?.skip) return null;

    if (error) {
        mbpLogger.logError({
            function: 'PLACollectionRecsABWrapper.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PLA',
            jsError: error,
        });
    }

    const sfData = wrapperData?.contentSF || null;
    const userGroup = sfData?.campaign?.campaignResponses?.[0]?.payload?.userGroup || 'control';
    const isNotControl = userGroup.toLowerCase() !== 'control';

    if (isNotControl && (brand?.id === '1001')) {
        return (
            <PLACollectionRecsContainer
                partNumber={partNumber}
                productName={productName}
                skuData={skuData}
                trackClickThrough={trackClickThrough}
            />
        );
    }

    // static collection recs from CMS
    return (
        <PLAPDPIconQuery
            plaRedesignDesktop={plaRedesignDesktop}
            brand={brand}
            partNumber={partNumber}
            productName={productName}
            trackClickThrough={trackClickThrough}
        />
    );
}

PLACollectionRecsABWrapper.propTypes = {
    brand: object,
    partNumber: string.isRequired,
    productName: array.isRequired,
    plaRedesignDesktop: bool,
    skuData: array,
};

PLACollectionRecsABWrapper.defaultProps = {
    brand: object,
    plaRedesignDesktop: bool,
    skuData: [],
};
